// Human-readable labels for all enum values used in the UI.
// Centralizing these here means changing a label only requires
// editing one place instead of hunting through components.

import type { Verdict, WhatToRead, PaperType, Priority, TierKey } from '../types';

export const VERDICT_LABELS: Record<Verdict, string> = {
  must_read: 'Must Read',
  worth_it: 'Worth It',
  skim: 'Skim It',
  skip: 'Desk Reject',
};

export const VERDICT_CONFIG: Record<Verdict, { label: string; varPrefix: string }> = {
  must_read: { label: 'Must Read', varPrefix: 'must' },
  worth_it: { label: 'Worth It', varPrefix: 'worth' },
  skim: { label: 'Skim It', varPrefix: 'skim' },
  skip: { label: 'Desk Reject', varPrefix: 'skip' },
};

export const WHAT_TO_READ_LABELS: Record<WhatToRead, string> = {
  full: 'Read the full paper',
  methodology_only: 'Read methodology only',
  abstract_only: 'Abstract is enough',
  skip: 'Skip entirely',
};

export const WHAT_TO_READ_SHORT: Record<WhatToRead, string> = {
  full: 'Full paper',
  methodology_only: 'Methods only',
  abstract_only: 'Abstract only',
  skip: 'Skip',
};

export const PAPER_TYPE_LABELS: Record<PaperType, string> = {
  original_research: 'Original Research',
  literature_review: 'Literature Review',
  methodology: 'Methodology Paper',
  theoretical: 'Theoretical / Conceptual',
  mixed: 'Mixed',
};

export const PAPER_TYPE_SHORT: Record<PaperType, string> = {
  original_research: 'Research',
  literature_review: 'Lit Review',
  methodology: 'Methods',
  theoretical: 'Theory',
  mixed: 'Mixed',
};

export const PRIORITY_OPTIONS: {
  value: Priority;
  label: string;
  description: string;
}[] = [
  {
    value: 'honest',
    label: 'Honest assessment',
    description: 'Balanced across all dimensions',
  },
  {
    value: 'rigorous',
    label: 'I need rigorous methodology',
    description: 'Weight methods and evidence heavily',
  },
  {
    value: 'novel',
    label: 'I need genuinely new ideas',
    description: 'Weight novelty and contribution heavily',
  },
  {
    value: 'readable',
    label: "I'm new to this field",
    description: 'Weight clarity and accessibility heavily',
  },
  {
    value: 'overview',
    label: 'I need a landscape overview',
    description: "A good lit review is fine — don't penalize for low novelty",
  },
];

export const TIER_LABELS: Record<TierKey, string> = {
  free: 'Abstracts',
  reading_list: 'Reading List',
  dissertation: 'Dissertation',
  lab: 'Lab',
};

/** Returns a score color CSS variable name based on score value */
export function getScoreColorVar(score: number): string {
  if (score >= 4) return 'var(--score-high)';
  if (score >= 3) return 'var(--score-mid)';
  return 'var(--score-low)';
}